// React
import { useCallback, useMemo } from 'react';

// Kendo
import { DropDownList as KendoDropDownList } from "@progress/kendo-react-dropdowns";

const DropDownList = (fieldRenderProps) => {
    const {
        data = [],
        value,
        onChange,
        textField = 'label',
        dataItemKey = 'value',
        allowNull = false,
        nullText = '-- Select --',
        ...others
    } = fieldRenderProps;

    // Add null option to data if allowNull is true
    const enhancedData = useMemo(() => {
        if (!allowNull) {
            return data;
        }

        const nullOption = {
            [textField]: nullText,
            [dataItemKey]: null
        };

        return [nullOption, ...data];
    }, [data, allowNull, textField, dataItemKey, nullText]);

    const selectedItem = useMemo(() => {
        if (!value || !enhancedData || enhancedData.length === 0) {
            // If allowNull is true and no value, return the null option
            return allowNull ? enhancedData[0] : null;
        }

        let searchValue = value;
        if (typeof value === 'object' && value !== null && value.hasOwnProperty(dataItemKey)) {
            searchValue = value[dataItemKey];
        }

        const foundItem = enhancedData.find(item => {
            if (typeof item === 'object' && item !== null) {
                return item[dataItemKey] === searchValue;
            }
            return item === searchValue;
        });

        return foundItem || (allowNull ? enhancedData[0] : null);
    }, [value, enhancedData, dataItemKey, allowNull]);

    const onValueChange = useCallback((event) => {
        const selectedItem = event.target.value;
        if (onChange && selectedItem) {
            let extractedValue;
            if (typeof selectedItem === 'object' && selectedItem !== null) {
                extractedValue = selectedItem[dataItemKey];
            }
            else {
                extractedValue = selectedItem;
            }

            onChange({
                target: {
                    value: extractedValue
                }
            });
        }
        else if (onChange) {
            onChange({
                target: {
                    value: null
                }
            });
        }
    }, [onChange, dataItemKey]);

    return (
        <KendoDropDownList
            {...others}
            value={selectedItem}
            onChange={onValueChange}
            data={enhancedData}
            textField={textField}
            dataItemKey={dataItemKey}
        />
    );
};

export default DropDownList;
